'use client';

import Link from 'next/link';
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
        return 'Posted today';
    } else if (diffDays <= 30) {
        return `Posted ${diffDays} days ago`;
    } else {
        return `Posted on ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
};

/**
 * PropertyCard Component
 * Modern, responsive card for displaying property listings
 */
const PropertyCard = ({
    property,
    onSave,
    params,
    listingType = 'buy',
    variant = 'default' // 'default' | 'horizontal' | 'compact'
}) => {
    const {
        id,
        title,
        price,
        priceUnit = 'Lac',
        pricePerSqft,
        type,
        bedrooms,
        bathrooms,
        area,
        areaUnit = 'sqft',
        locality,
        city,
        image,
        imageCount = 1,
        badges = [],
        isNew = false,
        isVerified = false,
        isSaved = false,
        //listingType = 'buy',
        // Rent Specific
        furnishing = '', // 'Furnished', 'Semi-Furnished', 'Unfurnished'
        tenantPreference = '', // 'Bachelors', 'Family', 'Any'
        availability = '', // 'Immediate', '15 Days'
        // Buy Specific
        possessionStatus = '', // 'Ready to Move', 'Under Construction'
        age = '', // 'New', '5 Years'

        // Civil Specific
        experience = '',
        rating = '',
        product_list = 'cement, bricks, blocks, sand, etc',

        // Plot Specific
        approval = '',
        dimensions = '',
        facing = '',
        boundary = '',
        isFeatured = false,
        created_at
    } = property;

    const displayTenantPreference = tenantPreference || property.tenants_preference || '';

    // Format price display
    const formatPrice = (price, unit) => {
        if (listingType === 'rent') {
            return `₹${price.toLocaleString('en-IN')}/mo`;
        }
        return `₹${price} ${unit}`;
    };

    const getImgSrc = (img) => {
        if (!img) return 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400';
        if (img.startsWith('http') || img.startsWith('data:')) return img;
        return BASE_URL + img;
    };

    const handleSave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onSave) onSave(id);
    };

    // Card Actions Component for reusability
    const CardActions = () => (
        <>
            <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10">
                {/* Status/Furnishing Badge */}
                {(listingType === 'rent' && furnishing) && (
                    <span className="px-2.5 py-1 text-xs font-semibold bg-violet-600 text-white rounded-full shadow-sm">
                        {furnishing}
                    </span>
                )}
                {(listingType === 'buy' && possessionStatus) && (
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${possessionStatus === 'Ready to Move' ? 'bg-green-600 text-white' : 'bg-orange-500 text-white'}`}>
                        {possessionStatus}
                    </span>
                )}

                {isVerified && (
                    <span className="px-2.5 py-1 text-xs font-semibold bg-white/90 backdrop-blur-sm text-green-700 rounded-full shadow-sm flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 4.5L4.5 10.5L1.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Verified
                    </span>
                )}
                {/* Badges loop... */}
                {isNew && (
                    <span className="px-2.5 py-1 text-xs font-semibold bg-indigo-600 text-white rounded-full shadow-sm">
                        New
                    </span>
                )}
                {/* {badges.map((badge, idx) => (
                    <span key={idx} className="px-2.5 py-1 text-xs font-semibold bg-white/90 backdrop-blur-sm text-slate-700 rounded-full shadow-sm">
                        {badge}
                    </span>
                ))} */}
            </div>

            <button
                onClick={handleSave}
                className={`absolute top-3 right-3 p-2 rounded-full shadow-md backdrop-blur-sm transition-all duration-300 z-10 hover:scale-110 active:scale-95 ${isSaved
                    ? 'bg-rose-500 text-white'
                    : 'bg-white/90 text-slate-500 hover:text-rose-500'
                    }`}
                title={isSaved ? "Remove from wishlist" : "Add to wishlist"}
            >
                <svg
                    className={`w-5 h-5 ${isSaved ? 'fill-current' : 'fill-none'}`}
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            </button>

            {imageCount > 1 && (
                <div className="absolute bottom-3 right-3 px-2 py-1 text-xs font-medium bg-black/60 text-white rounded-md flex items-center gap-1 backdrop-blur-sm z-10">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {imageCount}
                </div>
            )}
        </>
    );

    // Determine Link Path based on Listing Type
    const getDetailPath = () => {
        switch (listingType) {
            case 'buy': return `/property/${id}?${params}`;
            case 'rent': return `/rent/p/${id}?${params}`;
            case 'projects': return `/projects/p/${id}?${params}`;
            case 'plots': return `/plots/p/${id}?${params}`;
            case 'civil': return `/civil/p/${id}?${params}`;
            default: return `/property/${id}?${params}`; // Fallback
        }
    };

    const detailPath = getDetailPath();

    // Horizontal Variant (List View)
    if (variant === 'horizontal') {
        if (listingType === 'plots') {
            return (
                <Link
                    href={detailPath}
                    className="group relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs hover:shadow-md transition-all duration-300 flex flex-row gap-3 md:gap-6 items-center"
                >
                    {/* Image Section */}
                    <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 md:shrink-0 overflow-hidden rounded-xl md:rounded-2xl shrink-0">
                        <img
                            src={image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : '/no-image.jpeg'}
                            alt={title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />

                        {/* Top Badges */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                            {isFeatured && (
                                <span className="px-1.5 py-0.5 text-[9px] md:text-xs font-bold uppercase bg-yellow-400 text-yellow-900 rounded shadow-sm w-fit">
                                    Featured
                                </span>
                            )}
                            {approval && (
                                <span className="px-1.5 py-0.5 text-[9px] md:text-xs font-bold uppercase bg-white/95 text-primary rounded shadow-sm w-fit backdrop-blur-sm">
                                    {approval}
                                </span>
                            )}
                        </div>

                        {/* Bottom Badge (Plot Type) */}
                        {type && (
                            <div className="absolute bottom-2 left-2 z-10">
                                <span className="px-1.5 py-0.5 text-[9px] md:text-xs font-semibold bg-black/60 backdrop-blur-sm text-white rounded-full shadow-sm">
                                    {Array.isArray(type) ? type.join(', ') : type}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-0.5 md:py-1">

                        {/* Header Row */}
                        <div className="flex justify-between items-start">
                            <div className="min-w-0">
                                <div className="text-base sm:text-lg md:text-2xl font-bold text-primary truncate">
                                    ₹{price} <span className="text-xs sm:text-sm md:text-lg font-semibold">{priceUnit}</span>
                                    {pricePerSqft && (
                                        <span className="hidden md:inline-block text-xs text-slate-500 font-medium ml-2">
                                            Avg. ₹{pricePerSqft}/sq.ft
                                        </span>
                                    )}
                                </div>
                            </div>

                            {onSave && (
                                <button
                                    onClick={handleSave}
                                    className={`p-1.5 rounded-full hover:bg-slate-50 transition-all duration-300 shrink-0 ${isSaved ? 'text-rose-500' : 'text-slate-400 hover:text-rose-500'
                                        }`}
                                    title={isSaved ? "Remove from wishlist" : "Add to wishlist"}
                                >
                                    <svg
                                        className={`w-5 h-5 ${isSaved ? 'fill-current' : 'fill-none'}`}
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Title */}
                        <h3 className="text-xs sm:text-sm md:text-lg font-bold text-slate-800 line-clamp-1 group-hover:text-primary transition-colors mt-0.5 md:mt-1" title={title}>
                            {title}
                        </h3>

                        {/* Location & Posted Date */}
                        <div className="flex items-center justify-between gap-2 mt-1">
                            <p className="flex items-center gap-1.5 text-[10px] sm:text-xs md:text-sm text-slate-500 truncate">
                                <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{locality}, {city}</span>
                            </p>
                            {created_at && (
                                <span className="text-[10px] md:text-xs text-slate-400 shrink-0 bg-slate-50 px-2 py-0.5 rounded flex items-center gap-1">
                                    <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    {formatDate(created_at)}
                                </span>
                            )}
                        </div>

                        {/* Specs Grid */}
                        {/* Mobile Specs Row */}
                        <div className="flex items-center gap-3 text-slate-500 text-[10px] sm:text-xs mt-2 md:hidden">
                            <span className="flex items-center gap-1 font-medium">
                                <span className="text-slate-400">Area:</span> {area} {areaUnit}
                            </span>
                            {dimensions && (
                                <span className="flex items-center gap-1 font-medium">
                                    <span className="text-slate-400">Dim:</span> {dimensions}
                                </span>
                            )}
                        </div>

                        {/* Desktop Specs Grid */}
                        <div className="hidden md:grid grid-cols-3 gap-2 mt-4 bg-slate-50 rounded-xl p-3 border border-slate-100">
                            <div className="flex flex-col items-center justify-center p-1 border-r border-slate-200 last:border-0">
                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Plot Area</span>
                                <span className="text-base font-bold text-primary">{area}</span>
                                <span className="text-[10px] text-slate-400 font-medium">{areaUnit}</span>
                            </div>
                            <div className="flex flex-col items-center justify-center p-1 border-r border-slate-200 last:border-0">
                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Dimensions</span>
                                <span className="text-base font-bold text-slate-800">{dimensions}</span>
                                <span className="text-[10px] text-slate-400 font-medium">Feet</span>
                            </div>
                            <div className="flex flex-col items-center justify-center p-1">
                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Facing</span>
                                <span className="text-base font-bold text-slate-800">{facing}</span>
                                <span className="text-[10px] text-slate-400 font-medium">{boundary}</span>
                            </div>
                        </div>

                    </div>
                </Link>
            );
        }

        return (
            <Link
                href={detailPath}
                className="group relative p-2 md:p-3 bg-white border border-slate-100 rounded-2xl md:rounded-[20px] shadow-xs hover:shadow-md transition-all duration-300 flex flex-row gap-3 md:gap-6 items-center"
            >
                {/* Image Section */}
                <div className="relative w-[110px] h-[82px] xs:w-[125px] xs:h-[94px] md:w-60 md:h-40 md:shrink-0 overflow-hidden rounded-xl md:rounded-2xl shrink-0">
                    <img
                        src={getImgSrc(image)}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 md:opacity-0" />

                    {/* Top Badges */}
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1 z-10">
                        {isVerified && (
                            <span className="px-2 py-0.5 text-[9px] md:text-xs font-semibold bg-[#4caf50] text-white rounded-full shadow-sm flex items-center gap-1">
                                <svg className="w-2.5 h-2.5 md:w-3 md:h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.5 4.5L4.5 10.5L1.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Verified
                            </span>
                        )}
                        <div className="hidden md:flex flex-col gap-1">
                            {(listingType === 'rent' && furnishing) && (
                                <span className="px-2 py-0.5 text-[9px] md:text-xs font-semibold bg-violet-600 text-white rounded-full shadow-sm">
                                    {furnishing}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Image Count Badge */}
                    {imageCount > 1 && (
                        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 text-[9px] md:text-xs font-medium bg-black/60 text-white rounded flex items-center gap-1 backdrop-blur-sm z-10">
                            <svg className="w-2.5 h-2.5 md:w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {imageCount}
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="flex-1 min-w-0 flex flex-col h-full justify-between py-0.5 md:py-1">

                    {/* Top Row: Price and Save Button */}
                    <div className="flex justify-between items-start">
                        <div className="min-w-0">
                            {listingType !== 'civil' ? (
                                <p className="text-base sm:text-lg md:text-2xl font-bold text-primary truncate">
                                    {formatPrice(price, priceUnit)}
                                    {pricePerSqft && (
                                        <span className="hidden md:inline-block text-sm font-medium text-slate-500 ml-2">
                                            ₹{pricePerSqft.toLocaleString('en-IN')}/sqft
                                        </span>
                                    )}
                                </p>
                            ) : (
                                <p className="text-base sm:text-lg md:text-xl font-bold text-primary truncate">
                                    Civil Supplier
                                </p>
                            )}
                        </div>

                        <button
                            onClick={handleSave}
                            className={`p-1.5 rounded-full hover:bg-slate-50 transition-all duration-300 shrink-0 ${isSaved ? 'text-rose-500' : 'text-slate-400 hover:text-rose-500'
                                }`}
                            title={isSaved ? "Remove from wishlist" : "Add to wishlist"}
                        >
                            <svg
                                className={`w-5 h-5 ${isSaved ? 'fill-current' : 'fill-none'}`}
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </button>
                    </div>

                    {/* Title */}
                    <h3 className="text-xs sm:text-sm md:text-lg font-bold text-slate-800 line-clamp-1 group-hover:text-primary transition-colors mt-0.5 md:mt-1">
                        {title}
                    </h3>

                    {/* Civil listing special product list */}
                    {listingType === 'civil' && (
                        <p className="text-[10px] sm:text-xs text-slate-500 line-clamp-1 mt-0.5 capitalize">
                            {Array.isArray(product_list) ? product_list.join(', ') : product_list}
                        </p>
                    )}

                    {/* Location & Posted Date */}
                    <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="flex items-center gap-1 text-[10px] sm:text-xs md:text-sm text-slate-500 truncate">
                            <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{locality}, {city}</span>
                        </p>
                        {created_at && (
                            <span className="text-[10px] md:text-xs text-slate-400 shrink-0 bg-slate-50 px-2 py-0.5 rounded flex items-center gap-1">
                                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(created_at)}
                            </span>
                        )}
                    </div>

                    {/* Specs / Features Row */}
                    {listingType !== 'civil' && (
                        <>
                            {/* Mobile Specs Row */}
                            <div className="flex items-center gap-3 text-slate-500 text-[10px] sm:text-xs mt-2 md:hidden">
                                <span className="flex items-center gap-1 font-medium">
                                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                    </svg>
                                    {bedrooms}
                                </span>
                                <span className="flex items-center gap-1 font-medium">
                                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                    </svg>
                                    {bathrooms}
                                </span>
                                <span className="flex items-center gap-1 font-medium">
                                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                    {area}
                                </span>
                            </div>

                            {/* Desktop Specs Row */}
                            <div className="hidden md:flex flex-wrap gap-3 mt-4">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 text-sm font-medium">
                                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                    </svg>
                                    {bedrooms} Beds
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 text-sm font-medium">
                                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                    </svg>
                                    {bathrooms} Baths
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 text-sm font-medium">
                                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                    {area} {areaUnit}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Link>
        );
    }

    // Default Grid Variant
    if (listingType === 'plots') {
        return (
            <Link href={detailPath} className="group p-2 relative block h-full bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                {/* Image Container */}
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                    <img
                        src={image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : '/no-image.jpeg'}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />

                    {/* Top Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                        {isFeatured && (
                            <span className="px-2 py-1 text-xs font-bold uppercase bg-yellow-400 text-yellow-900 rounded shadow-sm w-fit">
                                Featured
                            </span>
                        )}
                        {approval && (
                            <span className="px-2 py-1 text-xs font-bold uppercase bg-white/90 text-primary rounded shadow-sm w-fit backdrop-blur-md">
                                {approval}
                            </span>
                        )}
                    </div>

                    {/* Bottom Badge (Plot Type) */}
                    {type && (
                        <div className="absolute bottom-3 left-3 z-10">
                            <span className="px-2.5 py-1 text-xs font-semibold bg-black/60 backdrop-blur-sm text-white rounded-full shadow-sm">
                                {Array.isArray(type) ? type.join(', ') : type}
                            </span>
                        </div>
                    )}

                    {onSave && (
                        <button
                            onClick={handleSave}
                            className={`absolute top-3 right-3 p-2 rounded-full shadow-md backdrop-blur-sm transition-all duration-300 z-10 hover:scale-110 active:scale-95 ${isSaved
                                ? 'bg-rose-500 text-white'
                                : 'bg-white/90 text-slate-500 hover:text-rose-500'
                                }`}
                            title={isSaved ? "Remove from wishlist" : "Add to wishlist"}
                        >
                            <svg
                                className={`w-5 h-5 ${isSaved ? 'fill-current' : 'fill-none'}`}
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Content Container */}
                <div className="p-1">
                    {/* Price & Title */}
                    <div className="mb-4">
                        <div className="flex justify-between items-baseline mb-1">
                            <p className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors">
                                {formatPrice(price, priceUnit)}
                            </p>
                            {pricePerSqft && (
                                <span className="text-xs font-medium text-slate-500">
                                    ₹{pricePerSqft}/sqft
                                </span>
                            )}
                        </div>
                        <h3 className="text-base font-semibold text-slate-700 line-clamp-1" title={title}>
                            {title}
                        </h3>
                    </div>

                    {/* Location & Posted Date */}
                    <div className="flex items-center justify-between gap-2 mb-5">
                        <div className="flex items-center gap-1.5 text-slate-500 text-sm truncate">
                            <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{locality}, {city}</span>
                        </div>
                        {created_at && (
                            <span className="text-[10px] text-slate-400 shrink-0 bg-slate-50 px-2 py-0.5 rounded flex items-center gap-1">
                                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(created_at)}
                            </span>
                        )}
                    </div>

                    {/* Specs Divider */}
                    <div className="h-px bg-slate-100 mb-4" />

                    {/* Specs Grid */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-[10px] text-slate-500 font-medium mb-0.5">Area</span>
                            <div className="flex flex-col items-center font-semibold text-slate-700 text-sm">
                                <span>{area}</span>
                                <span className="text-[9px] text-slate-400 font-medium">{areaUnit}</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-[10px] text-slate-500 font-medium mb-0.5">Dimensions</span>
                            <div className="flex flex-col items-center font-semibold text-slate-700 text-sm">
                                <span>{dimensions}</span>
                                <span className="text-[9px] text-slate-400 font-medium">Feet</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-[10px] text-slate-500 font-medium mb-0.5">Facing</span>
                            <div className="flex flex-col items-semibold items-center text-slate-700 text-sm">
                                <span>{facing}</span>
                                <span className="text-[9px] text-slate-400 font-medium">{boundary}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link href={`/property/${id}`} className="group p-2 relative block h-full bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            {/* Image Container */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                <img
                    src={getImgSrc(image)}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                <CardActions />
            </div>

            {/* Content Container */}
            <div className="p-1">
                {/* Price & Title */}
                <div className="mb-4">
                    <div className="flex justify-between items-baseline mb-1">
                        <p className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors">
                            {formatPrice(price, priceUnit)}
                        </p>
                        {pricePerSqft && (
                            <span className="text-xs font-medium text-slate-500">
                                ₹{pricePerSqft}/sqft
                            </span>
                        )}
                    </div>
                    <h3 className="text-base font-semibold text-slate-700 line-clamp-1" title={title}>
                        {title}
                    </h3>
                </div>

                {/* Location & Posted Date */}
                <div className="flex items-center justify-between gap-2 mb-5">
                    <div className="flex items-center gap-1.5 text-slate-500 text-sm truncate">
                        <svg className="w-4 h-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="truncate">{locality}, {city}</span>
                    </div>
                    {created_at && (
                        <span className="text-[10px] text-slate-400 shrink-0 bg-slate-50 px-2 py-0.5 rounded flex items-center gap-1">
                            <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {formatDate(created_at)}
                        </span>
                    )}
                </div>

                {/* Extra Details Row (Rent/Buy Specific) */}
                <div className="flex items-center gap-4 text-xs font-medium text-slate-500 mb-4 px-1">
                    {listingType === 'rent' && displayTenantPreference && (
                        <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {displayTenantPreference} Preferred
                        </div>
                    )}
                    {listingType === 'rent' && availability && (
                        <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {availability}
                        </div>
                    )}
                    {listingType === 'buy' && possessionStatus === 'Under Construction' && (
                        <div className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-1 rounded">
                            Possession: Dec 2025
                        </div>
                    )}
                    {listingType === 'buy' && possessionStatus === 'Ready to Move' && age && (
                        <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded">
                            Age: {age}
                        </div>
                    )}
                </div>

                {/* Specs Divider */}
                <div className="h-px bg-slate-100 mb-4" />

                {/* Specs Grid */}
                {listingType !== 'civil' && (
                    <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-xs text-slate-500 font-medium mb-0.5">Beds</span>
                            <div className="flex items-center gap-1 font-semibold text-slate-700 text-sm">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                </svg>
                                {bedrooms}
                            </div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-xs text-slate-500 font-medium mb-0.5">Baths</span>
                            <div className="flex items-center gap-1 font-semibold text-slate-700 text-sm">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                </svg>
                                {bathrooms}
                            </div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-slate-50 group-hover:bg-violet-50/50 transition-colors">
                            <span className="text-xs text-slate-500 font-medium mb-0.5">Area</span>
                            <div className="flex items-center gap-1 font-semibold text-slate-700 text-sm">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                                {area}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Link>
    );
};

export default PropertyCard;
